import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const LINKS = [
  { id: "about", label: "About" },
  { id: "collection", label: "Collection" },
  { id: "commissions", label: "Commissions" },
  { id: "disciplines", label: "Disciplines" },
];

export default function Navbar({ content }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 260 : 0);
  };

  const waDigits = content?.whatsapp?.replace(/[^0-9]/g, "");

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-cream/85 backdrop-blur-md border-b border-linen"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <button
            data-testid="nav-hero-link"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="text-left font-serif text-xl leading-[1.15] tracking-tight text-ink"
          >
            Artwith<span className="italic text-vermilion">.saivi</span>
            <span className="italic text-gild">saloni</span>
          </button>

          <nav className="hidden items-center gap-8 md:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}-link`}
                onClick={() => go(l.id)}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-clay transition-colors hover:text-vermilion"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="nav-contact-button"
              onClick={() => go("contact")}
              className="group rounded-full bg-umber px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-vermilion"
            >
              Get In Touch
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-linen bg-cream/70 text-ink transition-colors duration-300 hover:border-gild md:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="nav-drawer-backdrop"
              data-testid="nav-drawer-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[60] bg-umber/50 backdrop-blur-sm md:hidden"
            />
            <motion.aside
              key="nav-drawer"
              data-testid="nav-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
              className="fixed right-0 top-0 z-[61] flex h-full w-[82%] max-w-xs flex-col bg-cream shadow-[-20px_0_60px_-30px_rgba(24,19,16,0.4)] md:hidden"
            >
              <div className="flex items-center justify-between border-b border-linen px-6 py-4">
                <span className="font-serif text-lg tracking-tight text-ink">
                  Artwith<span className="italic text-vermilion">.saivi</span>
                  <span className="italic text-gild">saloni</span>
                </span>
                <button
                  data-testid="nav-drawer-close"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full text-ink transition-colors hover:text-vermilion"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <nav className="flex flex-col px-6 py-6">
                {LINKS.map((l, i) => (
                  <button
                    key={l.id}
                    data-testid={`nav-drawer-link-${l.id}`}
                    onClick={() => go(l.id)}
                    className="flex min-h-[56px] items-center justify-between border-b border-linen font-serif text-2xl tracking-tight text-ink transition-colors hover:text-vermilion"
                  >
                    {l.label}
                    <span className="font-mono text-[10px] tracking-[0.2em] text-sand">
                      0{i + 1}
                    </span>
                  </button>
                ))}
              </nav>

              <div className="mt-auto flex flex-col gap-3 px-6 pb-8">
                <button
                  data-testid="nav-drawer-contact-button"
                  onClick={() => go("contact")}
                  className="inline-flex min-h-[48px] items-center justify-center rounded-full bg-umber font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-vermilion"
                >
                  Get In Touch
                </button>
                {waDigits && (
                  <a
                    data-testid="nav-drawer-whatsapp-link"
                    href={`https://wa.me/${waDigits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-umber/25 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:border-gild"
                  >
                    <MessageCircle className="h-4 w-4 text-vermilion" />
                    WhatsApp
                  </a>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
